import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { catchError, filter, NEVER, of, Subscription, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../../../../services/user.service';
import { HowToService } from '../../services/how-to.service';
import { Tutorial, TutorialSaved } from '../../models/tutorial';
import { AsyncPipe } from '@angular/common';
import { NewTutorialComponent } from '../new-tutorial/new-tutorial.component';
import { MatIcon } from '@angular/material/icon';
import { openConfirmationDialog } from '../../../../components/confirmation-popup/confirmation-popup';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-edit-menu',
  templateUrl: './edit-menu.component.html',
  styleUrls: ['./edit-menu.component.scss'],
  imports: [AsyncPipe, MatIcon],
  standalone: true
})
export class EditMenuComponent implements OnInit, OnDestroy {
  @Input() tutorial: Tutorial | null = null;

  @HostListener('document:click', ['$event'])
  onGlobalClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isMenuOpened = false;
    }
  }

  canManageTutorials$ = of(false);
  isMenuOpened = false;

  private subscription = new Subscription();

  constructor(
    private userService: UserService,
    private elementRef: ElementRef,
    private dialog: MatDialog,
    private tutorialsService: HowToService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.canManageTutorials$ = this.userService
      .getUserInfo()
      .pipe(map(({ permissions }) => permissions.includes('Tutorials.Edit')));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  toggleMenu(event: Event) {
    event.stopPropagation();
    this.isMenuOpened = !this.isMenuOpened;
  }

  edit(event: Event) {
    event.stopPropagation();
    this.isMenuOpened = false;
    this.subscription.add(
      this.dialog
        .open(NewTutorialComponent, {
          minWidth: '500px',
          height: '80vh',
          data: { tutorial: this.tutorial, editMode: true }
        })
        .afterClosed()
        .pipe(
          tap((resp: TutorialSaved) => {
            if (resp === 'Saved') {
              this.tutorialsService.refreshTutorialList();
            }
          })
        )
        .subscribe()
    );
  }

  delete(event: Event) {
    event.stopPropagation();
    this.isMenuOpened = false;
    this.subscription.add(
      openConfirmationDialog(this.dialog, {
        title: 'Delete tutorial',
        message: 'Are you sure you want to delete this tutorial?'
      })
        .afterClosed()
        .pipe(
          filter(result => result === 'Confirmed'),
          switchMap(() => this.tutorialsService.delete(this.tutorial!)),
          tap(() => {
            this.snackBar.open('The tutorial was removed successfully', 'x', {
              verticalPosition: 'top',
              duration: 3000,
              panelClass: ['snackbar-green']
            });
            this.tutorialsService.refreshTutorialList();
          }),
          catchError(error => {
            console.error(error);
            this.snackBar.open('Something went wrong. Please try again later.', 'x', {
              verticalPosition: 'top',
              duration: 3000
            });
            return NEVER;
          })
        )
        .subscribe()
    );
  }
}
